import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Table, Badge } from 'react-bootstrap';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { api } from '../../../services/api';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#FF6B9D'];

export const AgentActivityReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [agentData, setAgentData] = useState<any>(null);

  useEffect(() => {
    fetchAgentData();
  }, []);

  const fetchAgentData = async () => {
    try {
      setLoading(true);
      const [ticketsResponse, usersResponse] = await Promise.all([
        api.get('/tickets'),
        api.get('/users')
      ]);
      
      const tickets = ticketsResponse.data.data || [];
      const users = usersResponse.data.data || [];
      
      // Calculate agent statistics
      const stats = calculateAgentStats(tickets, users);
      setAgentData(stats);
    } catch (error) {
      console.error('Failed to fetch agent data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAgentStats = (tickets: any[], users: any[]) => {
    // Filter agents
    const agents = users.filter(u => u.role === 'AGENT' || u.role === 'ADMIN');

    // Agent performance
    const agentPerformance = agents.map(agent => {
      const agentTickets = tickets.filter(t => t.assigneeId === agent.id);
      const resolved = agentTickets.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED').length;
      const inProgress = agentTickets.filter(t => t.status === 'IN_PROGRESS').length;
      const open = agentTickets.filter(t => t.status === 'OPEN').length;
      
      return {
        id: agent.id,
        name: agent.name,
        email: agent.email,
        totalTickets: agentTickets.length,
        resolved,
        inProgress,
        open,
        resolutionRate: agentTickets.length > 0 ? ((resolved / agentTickets.length) * 100).toFixed(1) : '0'
      };
    }).sort((a, b) => b.totalTickets - a.totalTickets);

    // Top 5 agents by tickets handled
    const topAgents = agentPerformance.slice(0, 5).map(agent => ({
      name: agent.name.split(' ')[0], // First name only for chart
      tickets: agent.totalTickets,
      resolved: agent.resolved
    }));

    // Agent workload distribution
    const workloadData = agentPerformance.slice(0, 6).map((agent, index) => ({
      name: agent.name.split(' ')[0],
      value: agent.totalTickets,
      fill: COLORS[index % COLORS.length]
    }));

    // Activity timeline (last 7 days)
    const activityTimeline = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      
      const dayTickets = tickets.filter(t => {
        const ticketDate = new Date(t.createdAt);
        return ticketDate.toDateString() === date.toDateString();
      });
      
      activityTimeline.push({
        date: dateStr,
        assigned: dayTickets.filter(t => t.assigneeId).length,
        resolved: dayTickets.filter(t => t.status === 'RESOLVED' || t.status === 'CLOSED').length,
      });
    }

    return {
      agentPerformance,
      topAgents,
      workloadData,
      activityTimeline,
      totalAgents: agents.length,
      activeAgents: agentPerformance.filter(a => a.totalTickets > 0).length,
    };
  };

  if (loading) {
    return (
      <Container fluid className="py-4 d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="mb-4">
        <h2 className="mb-1">
          <i className="fas fa-user-tie me-2 text-primary"></i>
          Agent Activity Reports
        </h2>
        <p className="text-muted mb-0 small">Monitor agent performance and workload distribution</p>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-primary rounded-circle p-3 me-3">
                  <i className="fas fa-users text-white"></i>
                </div>
                <div>
                  <h6 className="text-muted mb-1">Total Agents</h6>
                  <h3 className="mb-0">{agentData?.totalAgents || 0}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-success rounded-circle p-3 me-3">
                  <i className="fas fa-user-check text-white"></i>
                </div>
                <div>
                  <h6 className="text-muted mb-1">Active Agents</h6>
                  <h3 className="mb-0">{agentData?.activeAgents || 0}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="bg-info rounded-circle p-3 me-3">
                  <i className="fas fa-chart-line text-white"></i>
                </div>
                <div>
                  <h6 className="text-muted mb-1">Avg Tickets/Agent</h6>
                  <h3 className="mb-0">
                    {agentData?.activeAgents > 0 
                      ? Math.round(agentData?.agentPerformance.reduce((sum: number, a: any) => sum + a.totalTickets, 0) / agentData?.activeAgents)
                      : 0}
                  </h3>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row>
        {/* Top Agents Performance */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-trophy me-2 text-warning"></i>
                Top Agent Performance
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {agentData?.topAgents && agentData.topAgents.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={agentData.topAgents}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="tickets" fill="#0dcaf0" name="Total Tickets" />
                    <Bar dataKey="resolved" fill="#198754" name="Resolved" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">No data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Workload Distribution */}
        <Col lg={6} className="mb-4">
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-chart-pie me-2 text-primary"></i>
                Workload Distribution
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {agentData?.workloadData && agentData.workloadData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={agentData.workloadData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {agentData.workloadData.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-center py-5 text-muted">No data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Activity Timeline */}
        <Col lg={12} className="mb-4">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-chart-line me-2 text-info"></i>
                Agent Activity Timeline (Last 7 Days)
              </h6>
            </Card.Header>
            <Card.Body className="p-3">
              {agentData?.activityTimeline && (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={agentData.activityTimeline}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="assigned" stroke="#0dcaf0" strokeWidth={2} name="Assigned" />
                    <Line type="monotone" dataKey="resolved" stroke="#198754" strokeWidth={2} name="Resolved" />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Agent Performance Table */}
        <Col lg={12}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="mb-0">
                <i className="fas fa-table me-2 text-success"></i>
                Detailed Agent Performance
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              {agentData?.agentPerformance && agentData.agentPerformance.length > 0 ? (
                <Table hover responsive className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Agent</th>
                      <th>Email</th>
                      <th className="text-center">Total Tickets</th>
                      <th className="text-center">Open</th>
                      <th className="text-center">In Progress</th>
                      <th className="text-center">Resolved</th>
                      <th className="text-center">Resolution Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentData.agentPerformance.map((agent: any) => (
                      <tr key={agent.id}>
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="bg-primary rounded-circle p-2 me-2" style={{ width: '32px', height: '32px' }}>
                              <i className="fas fa-user text-white" style={{ fontSize: '0.8rem' }}></i>
                            </div>
                            <span className="fw-medium">{agent.name}</span>
                          </div>
                        </td>
                        <td><small className="text-muted">{agent.email}</small></td>
                        <td className="text-center">
                          <Badge bg="primary">{agent.totalTickets}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="warning">{agent.open}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="info">{agent.inProgress}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg="success">{agent.resolved}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg={parseFloat(agent.resolutionRate) >= 70 ? 'success' : 'warning'}>
                            {agent.resolutionRate}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center py-5 text-muted">No agent data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};



